import dayjs from "dayjs";
import { toNumberString } from "./common";
import { toNum } from "./conf";
import {
  PA_ABLE2TRANSLATE,
  PA_GROUP,
} from "../types/product-attribute/product-attribute";

const parseAge = (value?: string) => {
  const ls = value?.split("_") ?? [];
  return {
    ageTo: ls[2] ? toNum(ls[2]) : -1,
    ageFrom: ls[1] ? toNum(ls[1]) : -1,
  };
};
const includeAge = (s1?: string, s2?: string) => {
  // s1: CHILD_1_13 s2: CHILD_12
  const { ageFrom, ageTo } = parseAge(s1);
  const { ageFrom: ageFrom2 } = parseAge(s2);
  if (ageFrom === -1 && ageTo === -1) return false;
  return ageFrom2 >= ageFrom && ageFrom2 <= ageTo;
};

const isProdaDefaultPass = (proda: ProdaFE) => {
  return proda.type === "DEFAULT" && proda.name === "PASS";
};
const attachToAgeQuantityLs = ({
  ls,
  age,
  quantity,
}: {
  ls: AgeQuantityDTO[];
  age: string;
  quantity: number;
}) => {
  // find if exist
  // if exist then update else add
  const exist = ls.some((x) => x.age === age);
  if (exist) {
    return ls
      .map((x) => (x.age === age ? { ...x, quantity: quantity } : x))
      .filter((x) => x.quantity > 0);
  } else {
    return [...ls, { age: age, quantity: quantity }].filter(
      (x) => x.quantity > 0,
    );
  }
};
const calcSumOfAgeQuantityLs =
  (ageQuantities: AgeQuantityDTO[]) =>
  (filter: (x: AgeQuantityDTO) => boolean) =>
    ageQuantities
      .filter(filter)
      .map((x) => x.quantity)
      .reduce((a, v) => a + v, 0);

const parseAllChildAges = (state: BookingState): number[] => {
  const ageQuantities = findDefaultBooka(state.bookingAttributes)?.value
    ?.ageQuantities;
  if (!ageQuantities) return [] as number[];
  return ageQuantities
    .filter((x) => x.quantity && x.age.startsWith("CHILD"))
    .map((x) => parseAge(x.age)?.ageFrom)
    .filter((x) => x >= 0) as number[];
};

const applyQuantityBasedOnAgeQuantity = (
  bookingAttributes: BookingAttributeDTO[],
  ageQuantities: AgeQuantityDTO[],
) => {
  // *BOOKMARK*: Pass quantity of children
  const fSum = calcSumOfAgeQuantityLs(ageQuantities);
  for (const ba of bookingAttributes) {
    if (!ba.value) ba.value = {};
    if (ba.productAttribute.type === "DEFAULT") {
      ba.value.ageQuantities = ageQuantities;
    }
    switch (ba.productAttribute.name) {
      case "PERSON":
        ba.value.quantity = fSum((x) => !!x);
        break;
      case "Q_SIM":
        // Q_SIM tính theo người lớn
        ba.value.quantity = fSum((x) => x?.age === "ADULT");
        break;
      case "SIM":
        if (
          ba.productAttribute.type !== "DEFAULT" &&
          typeof ba?.value?.quantity === "number"
        ) {
          // *BOOKMARK*: CHILD KHÔNG MUA SIM
          // ba.value.quantity = fSum((x) => x?.age === "ADULT");
          ba.value.quantity = fSum((x) => !!x);
        }
        break;
      case "PREMIUM_PLUS":
        if (true) {
          //  || ba.value.quantity
          // PREMIUM PLUS là bắt buộc khi mua PASS
          // ba.value.quantity = Math.max(
          //   1,
          //   fSum((x) => !!x),
          // );
          ba.value.quantity = fSum((x) => !!x) ? 1 : undefined;
        }
        break;
      case "ADULT":
        ba.value.quantity = fSum(
          (x) =>
            x.age === "ADULT" ||
            !bookingAttributes.some((ba) =>
              includeAge(ba.productAttribute.value, x.age),
            ),
        );
        break;
      case "CHILD":
        ba.value.quantity = fSum((x) =>
          includeAge(ba.productAttribute.value, x.age),
        );
        break;
    }
  }
  return bookingAttributes;
};

export const applyStateBasedOnAgeQuantity = ({
  copied,
  copiedAgeQuantities,
}: {
  copied: BookingState;
  copiedAgeQuantities: AgeQuantityDTO[];
}) => {
  applyQuantityBasedOnAgeQuantity(
    copied.bookingAttributes,
    copiedAgeQuantities,
  );

  // *BOOKMARK*: Pass quantity of children
  // nếu là pass thì cập nhật số lượng cho từng loại product
  copied.childrenLs = copied.childrenLs?.map((childState) => {
    childState.bookingAttributes = applyQuantityBasedOnAgeQuantity(
      childState.bookingAttributes,
      copiedAgeQuantities,
    );
    return childState;
  });
};

const dpDateTime = (x?: BookingAttributeValueDTO, duration?: string) => {
  const f1 = (k?: string) => {
    // 20250131 => 31/01/2025
    if (!k) return;
    return dayjs(k, "YYYYMMDD");
  };
  const f2 = (k?: string) => {
    try {
      // 0830 => 08:30 AM
      if (!k) return;
      k = k + "";
      const hh = k.substring(0, 2);
      const mm = k.substring(2, 4);
      return hh + ":" + mm + " "; //+ (+hh > 12 ? "PM" : "AM")
    } catch (e) {
      console.error(e);
      return k ?? "";
    }
  };
  const dayjsFromDate = f1(x?.destDate);
  const plusDay =
    duration && duration.endsWith("d") ? +toNumberString(duration) : 0;
  return [
    [dayjsFromDate?.format("YYYY/MM/DD"), f2(x?.destTimeslot)]
      .filter(Boolean)
      .join(". "),
    plusDay > 0
      ? [
          dayjsFromDate?.add(plusDay, "day").format("YYYY/MM/DD"),
          f2(x?.destTimeslot),
        ]
          .filter(Boolean)
          .join(". ")
      : undefined,
  ]
    .filter(Boolean)
    .join(" ~ ");
};
const isBookaCanlendar = (x: BookingAttributeDTO) => {
  return !!PRODA_CALENDAR_CONF[x.productAttribute.name as ProdAttrNameCalendar];
};
const isPickLater = (x: BookingAttributeDTO) => {
  return !!PRODA_CALENDAR_CONF[x.productAttribute.name as ProdAttrNameCalendar]
    ?.pickLater;
};
const findDate = (state: BookingState) => {
  return dpDateTime(state.bookingAttributes.find(isBookaCanlendar)?.value);
};
const findCalendarConfig = (
  state: BookingState,
  thirdPartyConfig?: TCalendarConfig[],
): TCalendarConfig[] | undefined => {
  if (thirdPartyConfig === undefined) {
    try {
      const a = state?.bookingAttributes?.find(
        (x) => x.productAttribute.name === "CALENDAR_CONFIG",
      );
      if (a?.productAttribute?.value) {
        return JSON.parse(
          a.productAttribute.value ?? "{}",
        ) as TCalendarConfig[];
      }
    } catch (e) {
      console.error(e);
    }
  } else {
    return thirdPartyConfig;
  }
};

const parseAgeLs = (booking: BookingState) => {
  return (
    findDefaultBooka(booking?.bookingAttributes)?.value?.ageQuantities ?? []
  );
};

const findDefaultBooka = (bookingAttributes: BookingAttributeDTO[]) => {
  return bookingAttributes?.find((x) => x.productAttribute.type === "DEFAULT");
};

const findProductType = (bookingAttributes: BookingAttributeDTO[]) => {
  return ProdAttrUtils.findDefaultBooka(bookingAttributes)?.productAttribute
    ?.value;
};
const countPersonOfPass = (passState: BookingState) => {
  return (
    findDefaultBooka(passState.bookingAttributes)
      ?.value?.ageQuantities?.map((x) => x.quantity)
      ?.reduce((a, v) => a + v, 0) ?? 0
  );
};
const countPerson = (state: BookingState) => {
  // const count =
  //   findDefaultBooka(state.bookingAttributes)
  //     ?.value?.ageQuantities?.map((x) => x.quantity)
  //     ?.reduce((a, v) => a + v, 0) ?? 0;
  const count = [
    ...filterByName(state, "ADULT"),
    ...filterByName(state, "CHILD"),
    ...filterByName(state, "PERSON"),
  ]
    .map((x) => x.value?.quantity ?? 0)
    .reduce((a, v) => a + v, 0);
  return count;
};

const countAdult = (state: BookingState) => {
  const count = [...filterByName(state, "ADULT")]
    .map((x) => x.value?.quantity ?? 0)
    .reduce((a, v) => a + v, 0);
  return count;
};

const findByName = (
  state: {
    bookingAttributes: BookingAttributeDTO[];
  },
  name: ProdAttrName,
) => {
  return state.bookingAttributes.find((x) => x.productAttribute.name === name);
};
const findOptionalByName = (
  state: {
    bookingAttributes: BookingAttributeDTO[];
  },
  name: ProdAttrName,
) => {
  return state.bookingAttributes.find(
    (x) =>
      x.productAttribute.name === name &&
      x.productAttribute.type === "OPTIONAL",
  );
};
const filterByName = (state: BookingState, name: ProdAttrName) => {
  return state.bookingAttributes.filter(
    (x) => x.productAttribute.name === name,
  );
};

const findMinAdult = (bookingAttributes: BookingAttributeDTO[]) => {
  const b = findDefaultBooka(bookingAttributes);
  const meta = b?.productAttribute?.meta;
  if (meta) {
    const x = JSON.parse(meta) as PaDefaultMeta;
    return x.minGuestEnabled ? (x.minAdultNum ?? 0) : 0;
  }
  return 0;
};

export const ProdAttrUtils = {
  parseAllChildAges,
  findByName,
  findOptionalByName,
  countPerson,
  countAdult,
  countPersonOfPass,
  findMinAdult,
  AGE_QUANTITY: {
    calcSum: calcSumOfAgeQuantityLs,
  },
  findDefaultBooka,
  parseAgeLs,
  findProductType,
  PASS: {
    isDefaultPa: isProdaDefaultPass,
    findDefaultBooka: (bookingAttributes: BookingAttributeDTO[]) => {
      return bookingAttributes.find((x) =>
        isProdaDefaultPass(x.productAttribute),
      );
    },

    calcSumOfAgeQuantityLs,
    applyQuantityOfChildren: applyQuantityBasedOnAgeQuantity,
    findPeopleQty: (bookingAttributes: BookingAttributeDTO[]) => {
      return (
        ProdAttrUtils.findDefaultBooka(bookingAttributes)
          ?.value?.ageQuantities?.filter((x) => !!x.age)
          .reduce((a, v) => a + v.quantity, 0) || 0
      );
    },
    findAdultQty: (bookingAttributes: BookingAttributeDTO[]) => {
      return (
        ProdAttrUtils.findDefaultBooka(
          bookingAttributes,
        )?.value?.ageQuantities?.find((x) => x.age === "ADULT")?.quantity ?? 0
      );
    },
    findChidQty: (bookingAttributes: BookingAttributeDTO[]) => {
      return (
        ProdAttrUtils.findDefaultBooka(bookingAttributes)
          ?.value?.ageQuantities?.filter((x) => x.age.startsWith("CHILD"))
          .reduce((a, v) => a + v.quantity, 0) || 0
      );
    },
    findMaxProduct: (bookingState: BookingState): number => {
      return +(
        bookingState.bookingAttributes.find(
          (x) => x.productAttribute.name === "MAX_PRODUCTS",
        )?.productAttribute?.value ?? 6
      );
    },
  },
  CHILD: {
    parseAge: parseAge,
    includeAge: includeAge,
    attachToAgeQuantityLs: attachToAgeQuantityLs,
  },
  SIM: {
    dp: (x?: string) => {
      if (!x) return "";
      const [v, d] = x.split("_");
      return [
        ProdAttrUtils.SIM.dpValidity(d),
        ProdAttrUtils.SIM.dpDataPackage(v),
      ]
        .filter((x) => x)
        .join(" ");
    },
    dpValidity: (x: string) => {
      const MAX = 100000;
      // 30DAYS
      try {
        const num = toNumberString(x);
        if (+num >= MAX) {
          return ["Unlimited"];
        }
        return [num, +num > 1 ? "days" : "day"];
      } catch (e) {
        console.error(e);
        return ["", ""];
      }
    },
    dpDataPackage: (x: string) => {
      const MAX = 100000;
      // 30GB
      try {
        const num = toNumberString(x);
        if (+num >= MAX) {
          return ["Unlimited"];
        }
        return [num, x.substring(num.length)];
      } catch (e) {
        console.error(e);
        return ["", ""];
      }
    },
  },
  CALENDAR: {
    isProda: (x: ProdaFE) => {
      return !!PRODA_CALENDAR_CONF[x.name as ProdAttrNameCalendar];
    },
    isBooka: isBookaCanlendar,
    isPickLater,
    findDate,
    findCalendarConfig,
  },
  findMinPrice: (
    name: ProdAttrName,
    dtoLs?: ProductAttributeDTO[],
  ): number | undefined => {
    const ls = (dtoLs ?? []).filter((x) => x.name === name);
    if (!ls.length) return;
    const minPrice = Math.min(
      ...ls.flatMap((x) => {
        return (x.variants ?? [])
          .map((v) => toNum(v.price))
          .filter((x) => x !== null && x !== undefined && !isNaN(x));
      }),
    );
    return minPrice;
  },
  findMinChildPrice: (
    name: ProdAttrName,
    dtoLs?: ProductAttributeDTO[],
  ): number | undefined => {
    const ls = (dtoLs ?? []).filter((x) => x.name === name);
    if (!ls.length) return;

    const prices = ls.flatMap((x) =>
      (x.variants ?? [])
        .map((v) => toNum(v.price))
        .filter(
          (n): n is number => typeof n === "number" && isFinite(n) && n > 0,
        ),
    );

    if (!prices.length) return;

    const minPrice = Math.min(...prices);
    return isFinite(minPrice) && minPrice > 0 ? minPrice : undefined;
  },
  dpDateTime,
  TOUR: {
    getDpValue(
      ls: (ProdaFE | ProductAttributeDTO)[],
      name: ProdAttrNameTourInfo,
    ) {
      const v = ls.find((x) => x.name === name)?.value;
      return v ?? "";
    },
    extractHighlight: (ls?: (ProdaFE | ProductAttributeDTO)[]) => {
      return (ls ?? [])
        .filter((item) => item.type === "HIGHLIGHTS")
        .map((item) => item.value)
        .filter((value): value is string => value !== undefined);
    },
  },
};

export const PRODA_CALENDAR_CONF: Record<
  ProdAttrNameCalendar,
  {
    pickLater: boolean;
    pickTimeSlot: boolean;
  }
> = {
  MUSICAL_CALENDAR: {
    pickLater: false,
    pickTimeSlot: true,
  },
  DAY_TOUR_CALENDAR: {
    // pickLater: true,
    pickLater: false,
    pickTimeSlot: true,
  },
  NIGHT_TOUR_CALENDAR: {
    pickLater: false,
    pickTimeSlot: true,
  },
  TICKET_CALENDAR: {
    pickLater: true,
    pickTimeSlot: true,
  },
  PRIORITY_CALENDAR: {
    pickLater: true,
    pickTimeSlot: true,
  },
  ESIM_CALENDAR: {
    pickLater: false,
    pickTimeSlot: false,
  },
};

export const pickedProdAttr2Options = (
  x: BookingAttributeDTO,
  formatMoney: any,
  t?: (x: string) => string,
): OptionProps & {
  paName?: ProdAttrName;
} => {
  const pa = x?.productAttribute;
  const v = pa.value ?? "";
  const price = pa?.variants?.find((x) => x.type === "ADULT")?.price;

  return {
    displayName:
      (PA_ABLE2TRANSLATE.includes(pa.name) ? t?.(v) : v) +
      (PA_GROUP.ADDITIONAL_OPTIONS.includes(pa?.name) &&
      typeof price === "number" &&
      !!price
        ? ` (${price > 0 ? "+" : ""}${formatMoney(price)})`
        : ""),
    value: pa.id,
    paName: pa.name,
  };
};

export const nameOfProdAttr = (name: ProdAttrName): string => {
  switch (name) {
    case "ADULT":
      return "Adult";
    case "CHILD":
      return "Child";
    case "Q_VEHICLE":
      return "Vehicle";
    case "LUGGAGE":
      return "Luggage";
    case "PRIORITY_CALENDAR":
      return "Priority";
    default:
      return name;
  }
};

export const findPremiumPlusDpPrice = ({
  product,
}: {
  product?: ProductDetailResponse;
}) => {
  return product?.attributeList
    ?.find((x) => x.name === "PREMIUM_PLUS")
    ?.variants?.find((x) => x.price && x.type === "ADULT")?.price;
};

export const parseFreeForAges = (freeForAges?: string): number[] => {
  try {
    return (freeForAges ?? "")
      .split(",")
      .map((x) => {
        const [f, t] = x
          .trim()
          .split("_")
          .map((x) => +x);
        if (t === undefined || t === null) return [t];
        const ret = [];
        for (let i = f ?? 0; i <= t; i++) {
          ret.push(i);
        }
        return ret;
      })
      .flat();
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const baFilter = ({
  bookingAttributes,
  name,
  type = "OPTIONAL",
}: {
  bookingAttributes: BookingAttributeDTO[];
  name: ProdAttrName;
  type?: TProductAttributeType;
}) => {
  return bookingAttributes.filter(
    (x) => x.productAttribute.name === name && x.productAttribute.type === type,
  );
};

export const baFind = ({
  bookingAttributes,
  name,
  type = "OPTIONAL",
}: {
  bookingAttributes: BookingAttributeDTO[];
  name: ProdAttrName;
  type?: TProductAttributeType;
}) => {
  return bookingAttributes.find(
    (x) => x.productAttribute.name === name && x.productAttribute.type === type,
  );
};

export class BookingAttributeHelper {
  constructor(public bookingAttributes?: BookingAttributeDTO[]) {
    this.bookingAttributes = bookingAttributes;
  }
  filter(
    name: ProdAttrName,
    type: TProductAttributeType = "OPTIONAL",
  ): BookingAttributeDTO[] {
    return baFilter({
      bookingAttributes: this.bookingAttributes ?? [],
      name,
      type,
    });
  }

  find(
    name: ProdAttrName,
    type: TProductAttributeType = "OPTIONAL",
  ): BookingAttributeDTO | undefined {
    return baFind({
      bookingAttributes: this.bookingAttributes ?? [],
      name,
      type,
    });
  }
}
